import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import useReveal from "../hooks/useReveal";
import WindPalm from "../components/WindPalm";
import AnimatedText from "../components/AnimatedText";
import SEO from "../components/SEO";
import { GALLERY, PALMS } from "../mock";

const CATEGORIES = ["All", "Exterior", "Interiors", "Lifestyle", "Details"];

const Gallery = () => {
  const ref = useReveal();
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = useMemo(
    () => (active === "All" ? GALLERY : GALLERY.filter((g) => g.category === active)),
    [active]
  );

  return (
    <div ref={ref} className="bg-[#f5f0e6] overflow-x-clip">
      <SEO
        title="Gallery — Inside Casa Elio"
        description="A visual tour of Casa Elio: 5 luxury king suites, salt-water infinity pool, open-air villa exterior, and the long Pacific horizon at San Juan del Sur."
        path="/gallery"
      />
      <section className="relative pt-40 pb-12 md:pt-48 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-6">
          Gallery
        </div>
        <h1 className="reveal font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] text-[#1f2a24] max-w-5xl">
          <span className="block"><AnimatedText text="A visual tour" stagger={32} baseDelay={150} /></span>
          <span className="block opacity-80" style={{ fontStyle: "italic", fontWeight: 500 }}>
            <AnimatedText text="of the villa." stagger={30} baseDelay={550} />
          </span>
        </h1>

        <div className="reveal mt-10 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`text-[11px] uppercase tracking-[0.25em] px-4 py-2 border transition-colors ${
                active === c
                  ? "bg-[#2f4438] text-[#f5f0e6] border-[#2f4438]"
                  : "border-[#1f2a24]/25 text-[#1f2a24] hover:bg-[#ebe3d2]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {filtered.map((g, i) => {
            const tall = i % 5 === 1 || i % 5 === 4;
            return (
              <button
                key={g.src + i}
                onClick={() => setLightbox(g)}
                className="group relative overflow-hidden bg-[#ebe3d2] text-left img-hover"
                style={{ aspectRatio: tall ? "3 / 4" : "4 / 3" }}
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="reveal-img absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/80">
                    {g.category}
                  </div>
                  <div className="text-sm text-white">{g.caption}</div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-[fadeIn_.3s_ease]"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-5 right-5 text-white/80 hover:text-white"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <figure className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt={lightbox.caption}
              className="w-full max-h-[82vh] object-contain"
            />
            <figcaption className="mt-4 text-center text-[#e8dfca]">
              <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">
                {lightbox.category}
              </div>
              <div className="text-sm mt-1">{lightbox.caption}</div>
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
};

export default Gallery;
